/**
 * Kanban Card Component
 * Draggable ticket card for kanban board
 */

import React from 'react';
import { useDraggable } from '@dnd-kit/core';

import { GripVertical, MessageSquare } from 'lucide-react';
import { TicketListItem } from '@/types';
import { Avatar } from '@/components/common/Avatar';
import { Badge } from '@/components/common/Badge';
import {
  getPriorityColor,
  getTypeIcon,
  formatLabel,
} from '@/utils/helpers';
import { Link } from 'react-router-dom';
import { SLABadge } from './SLABadge';

interface KanbanCardProps {
  ticket: TicketListItem;
  compact?: boolean;
}

export function KanbanCard({ ticket, compact = false }: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: ticket.id });

  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer group"
    >
      <Link to={`/tickets/${ticket.id}`} className="block">
        {/* Drag handle & header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <button
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
              {...attributes}
              {...listeners}
              onClick={(e) => e.preventDefault()}
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <span className="text-xl flex-shrink-0">{getTypeIcon(ticket.type)}</span>
            <span className="text-xs font-mono text-gray-600 truncate">
              {ticket.ticket_key}
            </span>
          </div>
          <Badge className={getPriorityColor(ticket.priority)}>
            {formatLabel(ticket.priority)}
          </Badge>
        </div>

        {/* Title */}
        <h4 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
          {ticket.title}
        </h4>

        {/* Tags */}
        {ticket.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {ticket.tags.slice(0, 2).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: `${tag.color}20`,
                  color: tag.color,
                }}
              >
                {tag.name}
              </span>
            ))}
            {ticket.tags.length > 2 && (
              <span className="text-xs text-gray-500">+{ticket.tags.length - 2}</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            {ticket.assignee ? (
              <Avatar
                name={ticket.assignee.name}
                src={ticket.assignee.avatar_url}
                size="sm"
              />
            ) : (
              <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                ?
              </div>
            )}
            {ticket.comment_count > 0 && (
              <div className="flex items-center space-x-1 text-gray-500">
                <MessageSquare className="h-3 w-3" />
                <span className="text-xs">{ticket.comment_count}</span>
              </div>
            )}
          </div>

          <SLABadge ticket={ticket} compact={compact} />
        </div>
      </Link>
    </div>
  );
}
